import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import { podeEscrever, useAutenticacao } from '../lib/auth';
import {
  buscarAniversariantes,
  buscarMinhasEscalas,
  buscarPendentes,
  type Aniversariante,
  type EscalaPendente,
  type MinhaEscala,
} from '../lib/escalas';
import { buscarMidias, incorporarVideo, type Midia } from '../lib/midias';
import { buscarMinhasDemandas, type MinhaDemanda, type MinhaTarefa } from '../lib/quadro';
import { horaDe } from '../lib/agenda';
import { minhaPosicao, resumoDaSemana } from '../lib/equipes';
import { Alerta, Cartao } from '../componentes/Interface';
import Layout from '../componentes/Layout';
import BannerTopo from '../componentes/BannerTopo';
import CalendarioDoMes from '../componentes/CalendarioDoMes';

type Versiculo = { referencia: string; texto: string; dia: string };

type Saude = {
  agoraSP: string;
  hojeSP: string;
  banco: { conectado: boolean; dialeto: string | null; tabelas: string };
};

const NOME_DO_PAPEL: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Equipe de mídia',
  viewer: 'Liderança · leitura',
  lider: '12 da Igreja',
  lider_adm: '12 da Igreja · ADM',
  voluntario: 'Voluntário',
  membro: 'Membro',
};

type ResumoEquipe = {
  id: number;
  nome: string;
  pessoas: number;
  presentes: Record<string, number>;
};

export default function Painel() {
  const { usuario } = useAutenticacao();
  const [saude, setSaude] = useState<Saude | null>(null);
  const [escalas, setEscalas] = useState<MinhaEscala[]>([]);
  const [cards, setCards] = useState<number | null>(null);
  const [minhasDemandas, setMinhasDemandas] = useState<MinhaDemanda[]>([]);
  const [minhasTarefas, setMinhasTarefas] = useState<MinhaTarefa[]>([]);
  const [versiculo, setVersiculo] = useState<Versiculo | null>(null);
  const [palavras, setPalavras] = useState<Midia[]>([]);
  const [proximos, setProximos] = useState<{ titulo: string; inicio: string; local: string | null }[]>([]);
  const [pedido, setPedido] = useState<{ status: string; ministerio: string | null; resposta: string | null } | null>(null);
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [pendentes, setPendentes] = useState<EscalaPendente[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const [equipes, setEquipes] = useState<ResumoEquipe[]>([]);
  const [cultosDaSemana, setCultosDaSemana] = useState<{ chave: string; nome: string }[]>([]);
  const [posicao, setPosicao] = useState<
    { nome: string; posicaoSemana: number; posicaoMes: number; pontosMes: number }[]
  >([]);
  const [posicaoSub, setPosicaoSub] = useState<
    { nome: string; posicaoSemana: number; posicaoMes: number; pontosMes: number }[]
  >([]);
  const [quantasEquipes, setQuantasEquipes] = useState(0);
  const [quantosSubs, setQuantosSubs] = useState(0);

  // chamada da semana da equipe de quem lidera; quem nao lidera nada nao ve
  useEffect(() => {
    if (!usuario?.lideraEquipe) return;
    let vivo = true;
    resumoDaSemana()
      .then((r) => {
        if (!vivo) return;
        setEquipes(r.equipes);
        setCultosDaSemana(r.cultos);
      })
      .catch(() => undefined);
    // a posicao no placar: so o lugar da equipe dele, sem os numeros dos outros
    minhaPosicao()
      .then((r) => {
        if (!vivo) return;
        setPosicao(r.equipes);
        setPosicaoSub(r.subs ?? []);
        setQuantasEquipes(r.quantasEquipes ?? 0);
        setQuantosSubs(r.quantosSubs ?? 0);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [usuario?.lideraEquipe]);

  useEffect(() => {
    let vivo = true;
    api<Saude>('/health')
      .then((d) => vivo && setSaude(d))
      .catch((e: ErroApi) => vivo && setErro(e.message));

    api<Versiculo>('/versiculo')
      .then((d) => vivo && setVersiculo(d))
      .catch(() => undefined);

    buscarMinhasEscalas()
      .then((d) => vivo && setEscalas(d.escalas))
      .catch(() => undefined);

    // a rota e de quem serve em algum ministerio; membro comum nem chama
    if (usuario && usuario.papel !== 'membro') {
      buscarAniversariantes()
        .then((d) => vivo && setAniversariantes(d.aniversariantes))
        .catch(() => undefined);
    }

    // cobrar quem nao confirmou e trabalho de quem monta a escala
    if (usuario && (usuario.papel === 'admin' || usuario.papel === 'editor')) {
      buscarPendentes()
        .then((d) => vivo && setPendentes(d.pendentes))
        .catch(() => undefined);
    }

    buscarMidias(undefined, 'video')
      .then((d) => vivo && setPalavras(d.midias.slice(0, 3)))
      .catch(() => undefined);

    api<{ pedido: { status: string; ministerio: string | null; resposta: string | null } | null }>('/meu-pedido')
      .then((d) => vivo && setPedido(d.pedido))
      .catch(() => undefined);

    api<{ itens: { titulo: string; inicio: string; local: string | null }[] }>('/agenda/proximos?limite=4')
      .then((d) => vivo && setProximos(d.itens))
      .catch(() => undefined);

    // quadro e demandas: so quem tem acesso ao quadro (admin e equipe de midia)
    if (usuario?.podeQuadro) {
      api<{ quadros: { cards: number }[] }>('/quadros')
        .then((d) => vivo && setCards(d.quadros.reduce((soma, q) => soma + q.cards, 0)))
        .catch(() => undefined);

      buscarMinhasDemandas()
        .then((d) => {
          if (!vivo) return;
          setMinhasDemandas(d.cards);
          setMinhasTarefas(d.tarefas);
        })
        .catch(() => undefined);
    }
    return () => {
      vivo = false;
    };
  }, [usuario]);

  if (!usuario) return null;
  const primeiroNome = usuario.nome.trim().split(' ')[0] ?? usuario.nome;
  const aguardando = escalas.filter((e) => e.status === 'convocado').length;

  return (
    <Layout titulo={`Ola, ${primeiroNome}`} subtitulo="Bom te ver de novo por aqui.">
      {erro && (
        <div className="mb-4 max-w-xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <BannerTopo podeEditar={usuario.papel === 'admin' || usuario.papel === 'editor'} />

      <div className="mb-4 rounded-2xl bg-tinta-900 p-5 text-white shadow-cartao md:p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
          Versiculo do dia
        </p>
        {versiculo ? (
          <>
            <p className="mt-2 text-lg leading-relaxed md:text-xl">{versiculo.texto}</p>
            <p className="mt-2 text-sm text-white/60">{versiculo.referencia}</p>
          </>
        ) : (
          <p className="mt-2 text-sm text-white/50">Carregando...</p>
        )}
      </div>

      {pedido && pedido.status !== 'aprovada' && (
        <div className="mb-4 max-w-3xl">
          <Alerta tipo={pedido.status === 'pendente' ? 'aviso' : 'erro'}>
            {pedido.status === 'pendente' ? (
              <>
                Seu pedido para servir {pedido.ministerio ? `no ${pedido.ministerio}` : 'em um ministério'} esta
                esperando a lideranca aprovar. Assim que aprovarem, a area aparece aqui sozinha.
              </>
            ) : (
              <>
                Seu pedido para servir {pedido.ministerio ? `no ${pedido.ministerio}` : 'em um ministério'} nao foi
                aprovado{pedido.resposta ? `: ${pedido.resposta}` : '.'} Voce continua com acesso de membro.
              </>
            )}
          </Alerta>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Cartao className="flex items-center gap-4">
              <span className="text-3xl font-bold text-tinta-900">{escalas.length}</span>
              <span className="text-sm leading-tight text-tinta-400">
                escalas
                <br />
                pela frente
              </span>
            </Cartao>
            <Cartao className="flex items-center gap-4">
              <span className="text-3xl font-bold text-tinta-900">
                {usuario.papel === 'membro' ? aguardando : (cards ?? '—')}
              </span>
              <span className="text-sm leading-tight text-tinta-400">
                {usuario.papel === 'membro' ? (
                  <>
                    aguardando
                    <br />
                    sua resposta
                  </>
                ) : (
                  <>
                    demandas
                    <br />
                    no quadro
                  </>
                )}
              </span>
            </Cartao>
          </div>

          {usuario.papel === 'membro' && (
            <Cartao>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold text-tinta-900">Próximos cultos</h2>
                <Link to="/agenda" className="text-sm text-tinta-400 hover:text-tinta-900">
                  ver calendario
                </Link>
              </div>
              {proximos.length === 0 ? (
                <p className="text-sm text-tinta-400">Nenhum culto marcado por enquanto.</p>
              ) : (
                <ul className="divide-y divide-tinta-200">
                  {proximos.map((e) => (
                    <li key={`${e.titulo}-${e.inicio}`} className="flex items-center justify-between gap-3 py-2.5">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-tinta-900">{e.titulo}</span>
                        <span className="block text-xs text-tinta-400">
                          {new Intl.DateTimeFormat('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit',
                          }).format(new Date(e.inicio))}{' '}
                          · {horaDe(e.inicio)}
                          {e.local ? ` · ${e.local}` : ''}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>
          )}

          {usuario.papel !== 'membro' && (
          <Cartao>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold text-tinta-900">Suas próximas escalas</h2>
              <Link to="/minhas-escalas" className="text-sm text-tinta-400 hover:text-tinta-900">
                ver todas
              </Link>
            </div>
            {escalas.length === 0 ? (
              <p className="text-sm text-tinta-400">
                Voce nao tem escala marcada. Quando entrar em alguma, ela aparece aqui e no seu
                calendario.
              </p>
            ) : (
              <ul className="divide-y divide-tinta-200">
                {escalas.slice(0, 4).map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-tinta-900">{e.titulo}</span>
                      <span className="block text-xs text-tinta-400">
                        {e.inicio
                          ? `${new Intl.DateTimeFormat('pt-BR', {
                              timeZone: 'America/Sao_Paulo',
                              day: '2-digit',
                              month: '2-digit',
                            }).format(new Date(e.inicio))} · ${horaDe(e.inicio)}`
                          : ''}{' '}
                        · {e.funcao}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-lg px-2 py-0.5 text-xs font-semibold ${
                        e.status === 'confirmado'
                          ? 'bg-tinta-900 text-white'
                          : e.status === 'indisponivel'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-tinta-100 text-tinta-600'
                      }`}
                    >
                      {e.status === 'convocado' ? 'responder' : e.status === 'confirmado' ? 'confirmado' : 'não pode'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
          )}

          {(minhasDemandas.length > 0 || minhasTarefas.length > 0) && (
            <Cartao>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold text-tinta-900">Minhas demandas</h2>
                <Link to="/quadros" className="text-sm text-tinta-400 hover:text-tinta-900">
                  ver quadros
                </Link>
              </div>
              <ul className="divide-y divide-tinta-200">
                {minhasDemandas.map((c) => (
                  <li key={`card-${c.id}`} className="py-2.5">
                    <Link
                      to={`/quadro/${c.quadroChave}`}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-tinta-900">{c.titulo}</span>
                        <span className="block text-xs text-tinta-400">
                          {c.quadroNome} · {c.colunaNome}
                          {c.entregaEm
                            ? ` · entrega ${new Intl.DateTimeFormat('pt-BR', {
                              timeZone: 'America/Sao_Paulo',
                              day: '2-digit',
                              month: '2-digit',
                            }).format(new Date(c.entregaEm))}`
                            : ''}
                        </span>
                      </span>
                      {c.prioridade === 'urgente' && (
                        <span className="shrink-0 rounded-lg bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                          urgente
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
                {minhasTarefas.map((t) => (
                  <li key={`tarefa-${t.id}`} className="py-2.5">
                    <Link
                      to={`/quadro/${t.quadroChave}`}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-tinta-900">{t.texto}</span>
                        <span className="block text-xs text-tinta-400">tarefa · {t.cardTitulo}</span>
                      </span>
                      {t.prioridade === 'urgente' && (
                        <span className="shrink-0 rounded-lg bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                          urgente
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {pendentes.length > 0 && (
            <Cartao>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold text-tinta-900">Ainda não confirmaram</h2>
                <Link to="/escala-cultos" className="text-sm text-tinta-400 hover:text-tinta-900">
                  ver escalas
                </Link>
              </div>
              <ul className="space-y-1">
                {pendentes.slice(0, 6).map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-amber-50 px-3 py-1.5 text-sm"
                  >
                    <span className="font-medium text-tinta-900">{p.membroNome}</span>
                    <span className="text-tinta-400">
                      {p.funcao} · {p.eventoTitulo}
                    </span>
                    {p.eventoInicio && (
                      <span className="ml-auto shrink-0 text-xs text-tinta-400">
                        {new Intl.DateTimeFormat('pt-BR', {
                          timeZone: 'America/Sao_Paulo',
                          day: '2-digit',
                          month: '2-digit',
                        }).format(new Date(p.eventoInicio))}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {pendentes.length > 6 && (
                <p className="mt-2 text-xs text-tinta-400">
                  e mais {pendentes.length - 6} pessoa(s).
                </p>
              )}
            </Cartao>
          )}

          {aniversariantes.length > 0 && (
            <Cartao>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold text-tinta-900">Aniversariantes do mês</h2>
                <span className="text-sm text-tinta-400">{aniversariantes.length}</span>
              </div>
              <ul className="space-y-1">
                {aniversariantes.map((a) => (
                  <li
                    key={a.id}
                    className={`flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm ${
                      a.ehHoje ? 'bg-tinta-900 text-white' : 'bg-tinta-100 text-tinta-800'
                    }`}
                  >
                    <span
                      className={`w-7 shrink-0 text-center text-xs font-semibold ${
                        a.ehHoje ? 'text-white' : 'text-tinta-400'
                      }`}
                    >
                      {String(a.dia).padStart(2, '0')}
                    </span>
                    <span className="truncate font-medium">{a.nome}</span>
                    {a.fazendo !== undefined && (
                      <span className="shrink-0 text-xs text-tinta-400">{a.fazendo} anos</span>
                    )}
                    {a.ehHoje && (
                      <span className="ml-auto shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-tinta-900">
                        hoje
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          <Cartao>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-semibold text-tinta-900">Últimas palavras</h2>
              <Link to="/palavras" className="text-sm text-tinta-400 hover:text-tinta-900">
                ver todas
              </Link>
            </div>
            {palavras.length === 0 ? (
              <p className="text-sm text-tinta-400">
                Nenhuma palavra publicada ainda. Quando o pastor publicar, ela aparece aqui.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                {palavras.map((v) => {
                  const incorporado = v.link ? incorporarVideo(v.link) : null;
                  return (
                    <Link
                      key={v.id}
                      to="/palavras"
                      className="block overflow-hidden rounded-xl bg-tinta-100 transition hover:bg-tinta-200"
                    >
                      <span className="flex aspect-vídeo items-center justify-center bg-tinta-900 text-2xl text-white">
                        {incorporado || v.caminho ? '▶' : '🎧'}
                      </span>
                      <span className="block px-2.5 py-2">
                        <span className="block truncate text-sm font-medium text-tinta-900">
                          {v.titulo ?? 'Palavra'}
                        </span>
                        <span className="block truncate text-xs text-tinta-400">
                          {v.eventoTitulo ?? ''}
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Cartao>
        </div>

        <div className="space-y-4">
          {usuario.papel !== 'membro' && (
            <CalendarioDoMes
              compromissos={escalas.map((e) => ({
                inicio: e.inicio,
                titulo: e.titulo,
                eventoId: e.eventoId,
                funcao: e.funcao,
                status: e.status,
              }))}
            />
          )}

          {usuario.lideraEquipe && equipes.length > 0 && (
            <Cartao>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-tinta-900">Chamada da semana</h2>
                <Link to="/equipes" className="text-xs font-semibold text-tinta-900 hover:underline">
                  abrir
                </Link>
              </div>
              {posicaoSub
                .filter((p) => p.pontosMes > 0)
                .map((p) => (
                  <div
                    key={`sub-${p.nome}`}
                    className="mt-3 flex items-center gap-3 rounded-xl bg-tinta-900 px-4 py-3 text-white"
                  >
                    <span className="text-2xl font-black">{p.posicaoMes}º</span>
                    <span className="text-sm">
                      <span className="block font-semibold">
                        {p.posicaoMes === 1 ? `${p.nome} está na frente!` : `Seu sub, ${p.nome}`}
                      </span>
                      <span className="block text-white/70">
                        de {quantosSubs} subs · {p.pontosMes.toFixed(1).replace('.', ',')} pontos ·{' '}
                        {p.posicaoSemana}º nesta semana
                      </span>
                    </span>
                  </div>
                ))}
              {posicao.filter((p) => p.pontosMes > 0).map((p) => (
                <div
                  key={p.nome}
                  className="mt-3 flex items-center gap-3 rounded-xl bg-tinta-900 px-4 py-3 text-white"
                >
                  <span className="text-2xl font-black">{p.posicaoMes}º</span>
                  <span className="text-sm">
                    <span className="block font-semibold">
                      {p.posicaoMes === 1 ? 'Sua equipe está na frente!' : 'Sua equipe no mês'}
                    </span>
                    <span className="block text-tinta-300">
                      de {quantasEquipes} equipes · {p.pontosMes.toFixed(1).replace('.', ',')} pontos
                      · {p.posicaoSemana}º nesta semana
                    </span>
                  </span>
                </div>
              ))}
              {equipes.map((e) => (
                <div key={e.id} className="mt-3">
                  <p className="text-sm font-semibold text-tinta-900">
                    {e.nome}{' '}
                    <span className="font-normal text-tinta-400">· {e.pessoas} pessoas</span>
                  </p>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {cultosDaSemana.map((c) => (
                      <div key={c.chave} className="rounded-xl bg-tinta-50 px-2 py-2 text-center">
                        <span className="block text-[10px] uppercase tracking-wide text-tinta-400">
                          {c.nome}
                        </span>
                        <span className="block text-base font-bold text-tinta-900">
                          {e.presentes[c.chave] ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Cartao>
          )}

          <Cartao>
            <h2 className="font-semibold text-tinta-900">Atalhos</h2>
            <div className="mt-3 grid gap-2">
              {(usuario.papel === 'admin' || usuario.papel === 'editor' || usuario.papel === 'viewer' || usuario.podeQuadro) && (
                <Link
                  to="/quadros"
                  className="rounded-xl bg-tinta-900 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-tinta-700"
                >
                  Abrir os quadros
                </Link>
              )}
              <Link
                to="/agenda"
                className="rounded-xl border border-tinta-200 px-4 py-2.5 text-center text-sm font-semibold text-tinta-700 transition hover:bg-tinta-100"
              >
                Calendário
              </Link>
              {usuario.papel !== 'membro' && (
                <Link
                  to="/minhas-escalas"
                  className="rounded-xl border border-tinta-200 px-4 py-2.5 text-center text-sm font-semibold text-tinta-700 transition hover:bg-tinta-100"
                >
                  Minhas escalas
                </Link>
              )}
              {usuario.lideraEquipe && (
                <Link
                  to="/equipes"
                  className="rounded-xl border border-tinta-200 px-4 py-2.5 text-center text-sm font-semibold text-tinta-700 transition hover:bg-tinta-100"
                >
                  Minha equipe
                </Link>
              )}
              <Link
                to="/palavras"
                className="rounded-xl border border-tinta-200 px-4 py-2.5 text-center text-sm font-semibold text-tinta-700 transition hover:bg-tinta-100"
              >
                Palavras
              </Link>
              <Link
                to="/fotos"
                className="rounded-xl border border-tinta-200 px-4 py-2.5 text-center text-sm font-semibold text-tinta-700 transition hover:bg-tinta-100"
              >
                Fotos dos cultos
              </Link>
            </div>
            {usuario.papel !== 'membro' && !podeEscrever(usuario.papel) && (
              <p className="mt-3 text-xs text-tinta-400">Seu acesso é de leitura.</p>
            )}
          </Cartao>

          <Cartao>
            <h2 className="font-semibold text-tinta-900">Sua conta</h2>
            <dl className="mt-3 space-y-1.5 text-sm">
              <Linha rotulo="E-mail" valor={usuario.email} />
              <Linha rotulo="Papel" valor={NOME_DO_PAPEL[usuario.papel] ?? usuario.papel} />
              {saude && <Linha rotulo="Agora em SP" valor={saude.agoraSP.slice(11, 16)} />}
            </dl>
            <p className="mt-3 text-xs text-tinta-400">
              A sessao se renova sozinha enquanto voce trabalha.
            </p>
          </Cartao>
        </div>
      </div>
    </Layout>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-tinta-200 pb-1.5">
      <dt className="text-tinta-400">{rotulo}</dt>
      <dd className="truncate font-medium text-tinta-900">{valor}</dd>
    </div>
  );
}
